/**
 * Google Workspace authentication provider.
 *
 * Uses `@react-oauth/google` with the implicit flow to obtain an access token
 * that the backend validates against Google's userinfo endpoint.
 *
 * Token refresh: Google's implicit flow tokens expire after ~1 hour with no
 * silent refresh mechanism.  When the token expires we set `isAuthenticated`
 * to false so `ProtectedRoute` will prompt the user to sign in again.
 */

import { type TokenResponse, useGoogleLogin } from "@react-oauth/google";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setTokenGetter } from "@/lib/api";
import type { IAuthContext } from "./types";
import { useProfileLoader } from "./useProfileLoader";

/** How many ms before expiry to proactively clear the token (5 min buffer). */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const STORAGE_KEY = "google_auth_token";

interface StoredToken {
  tokenResponse: TokenResponse;
  /** Absolute timestamp (ms) when the token expires. */
  expiresAt: number;
}

function persistToken(response: TokenResponse): void {
  const expiresAt = Date.now() + (response.expires_in ?? 3600) * 1000;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ tokenResponse: response, expiresAt }));
}

function loadPersistedToken(): TokenResponse | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredToken = JSON.parse(raw);
    // Check if the token is still valid (with buffer).
    if (Date.now() >= stored.expiresAt - EXPIRY_BUFFER_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Recalculate expires_in from the stored absolute time.
    const remainingSec = Math.floor((stored.expiresAt - Date.now()) / 1000);
    return { ...stored.tokenResponse, expires_in: remainingSec };
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function clearPersistedToken(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

// Note: the early token getter is registered inside useGoogleAuth via a
// ref-guarded one-time effect so it doesn't execute at import time in
// test/SSR contexts.

export function useGoogleAuth(): IAuthContext {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // The raw token response from Google (contains access_token + expires_in).
  const [tokenResponse, setTokenResponse] = useState<TokenResponse | null>(() => loadPersistedToken());

  const { user, profile, platformBlocked, fetchAndSetProfile, clearProfile, updatePreferences } = useProfileLoader();

  // Timer ref for proactive token expiry.
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard to ensure the early token getter is registered only once.
  const earlyTokenRegistered = useRef(false);

  // Derived state.
  const isAuthenticated = tokenResponse !== null;

  // --- Early token getter (one-time, on mount) ------------------------------
  // Register a token getter so that API calls during the first render cycle
  // already have an Authorization header.  The reactive effect below will
  // overwrite this once getAccessToken stabilises.
  useEffect(() => {
    if (earlyTokenRegistered.current) return;
    earlyTokenRegistered.current = true;
    const persisted = loadPersistedToken();
    if (persisted) {
      setTokenGetter(async () => {
        const t = loadPersistedToken();
        if (!t) throw new Error("No authenticated account");
        return t.access_token;
      });
    }
  }, []);

  // --- Token getter (returns the access_token for Bearer header) -------------

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!tokenResponse) {
      throw new Error("No authenticated account");
    }
    return tokenResponse.access_token;
  }, [tokenResponse]);

  // Register token getter for the API client.
  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // --- Token expiry management -----------------------------------------------

  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    if (tokenResponse?.expires_in) {
      const expiresInMs = tokenResponse.expires_in * 1000;
      const timeoutMs = Math.max(expiresInMs - EXPIRY_BUFFER_MS, 0);

      expiryTimerRef.current = setTimeout(() => {
        console.warn("[GoogleAuth] Token expired, clearing session.");
        clearPersistedToken();
        setTokenResponse(null);
        clearProfile();
      }, timeoutMs);
    }

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
  }, [tokenResponse, clearProfile]);

  // --- Login -----------------------------------------------------------------

  const googleLogin = useGoogleLogin({
    onSuccess: (response) => {
      persistToken(response);
      // Register the token getter synchronously BEFORE setting state, so
      // that when downstream contexts react to isAuthenticated flipping to
      // true, the API client already has a valid token getter.  Without
      // this, effects in contexts mounted above ProtectedRoute can fire
      // API calls before the useEffect-based token getter registration runs.
      setTokenGetter(async () => response.access_token);
      setTokenResponse(response);
      setError(null);
    },
    onError: (errorResponse) => {
      console.error("Google login failed:", errorResponse);
      setError(new Error(errorResponse.error_description || "Google login failed"));
      setIsLoading(false);
    },
    // Request openid + profile + email for authentication, plus read-only
    // Google Drive access so the backend can fetch documents on behalf of the user.
    scope: "openid profile email https://www.googleapis.com/auth/drive.readonly",
    flow: "implicit",
  });

  const login = useCallback(async () => {
    googleLogin();
  }, [googleLogin]);

  // --- Logout ----------------------------------------------------------------

  const logout = useCallback(async () => {
    clearPersistedToken();
    setTokenResponse(null);
    clearProfile();
    // Google doesn't have a server-side logout for implicit flow.
    // Clearing the token is sufficient — the user will need to re-consent.
  }, [clearProfile]);

  // --- Profile loading -------------------------------------------------------

  useEffect(() => {
    async function loadProfile() {
      if (!isAuthenticated) return;
      try {
        setIsLoading(true);
        setError(null);
        await fetchAndSetProfile();
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setError(err instanceof Error ? err : new Error("Failed to load profile"));
      } finally {
        setIsLoading(false);
      }
    }
    loadProfile();
  }, [isAuthenticated, fetchAndSetProfile]);

  // Resolve initial loading state.
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // --- Refresh profile -------------------------------------------------------

  const refreshProfile = useCallback(async () => {
    try {
      await fetchAndSetProfile();
    } catch (err) {
      console.error("Failed to refresh profile:", err);
    }
  }, [fetchAndSetProfile]);

  return useMemo<IAuthContext>(
    () => ({
      user,
      profile,
      platformBlocked,
      isAuthenticated,
      isLoading,
      error,
      login,
      logout,
      getAccessToken,
      refreshProfile,
      updatePreferences,
      providerName: "Google",
    }),
    [
      user,
      profile,
      platformBlocked,
      isAuthenticated,
      isLoading,
      error,
      login,
      logout,
      getAccessToken,
      refreshProfile,
      updatePreferences,
    ],
  );
}
